import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {getChildrenText, getNid, prefix} from "../../wui-core/src/index"
import {getLangInfo} from "../../wui-locale/src/tool";
import i18n from './i18n.js';
import Modal from '../../wui-modal/src'
import {WithConfigConsumer} from "../../wui-provider/src/context";
import getFileItem from './getFileItem';
import RcUpload from './Upload';
import UploadList from './uploadList';

import { EntranceProps, DefaultListArr } from './iUpload'

function T() {
    return true;
}

// Fix IE file.status problem
// via coping a new Object
function fileToObject(file: DefaultListArr) {
    return {
        lastModified: file.lastModified,
        lastModifiedDate: file.lastModifiedDate,
        name: file.filename || file.name,
        size: file.size,
        type: file.type,
        uid: file.uid,
        response: file.response,
        error: file.error,
        percent: 0,
        originFileObj: file,
        status: '',
    };
}

/**
 * 生成Progress percent: 0.1 -> 0.98 (优化版本)
 */
function genPercentAdd() {
    let k = 0.1;
    const i = 0.01;
    const end = 0.98;
    return function(s: number) {
        let start = s;
        if (start >= end) {
            return start;
        }

        start += k;
        k = k - i;
        if (k < 0.001) {
            k = 0.001;
        }
        return start * 100;
    };
}

function Dragger(props: Partial<EntranceProps>) {
    // @ts-ignore
    return <Upload {...props} type="drag" style={{height: props.height}}/>;
}

Dragger.propTypes = {
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

function getBase64(file: any) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

const defaultProps = {
    clsPrefix: `${prefix}-upload`,
    type: 'select',
    multiple: false, // 是否支持多文件上传 兼容ie10以上
    action: '', // 上传的服务器地址
    data: {}, // 上传所需参数或返回上传参数的方法
    accept: '', // 接受上传的文件类型
    beforeUpload: T, // 上传文件之前的钩子
    showUploadList: true, // 是否展示文件列表, 可设为一个对象，用于单独设定 `showPreviewIcon`、 `showDownloadIcon` 和 `showRemoveIcon`
    listType: 'text', // or pictrue 上传列表的内建样式，支持三种基本样式 text, picture 和 picture-card
    className: '',
    disabled: false, // 是否禁用，设置为 true 后前端禁止删除已上传文件
    supportServerRender: true, // 服务端渲染时需要打开这个
    preventDefaultPreview: false,
    enterDragger: () => {
    }, // 拖拽上传，当鼠标拖拽文件进入 Dragger 区域时触发
    leaveDragger: () => {
    }, // 拖拽上传，当鼠标拖拽文件离开 Dragger 区域时触发
    defaultFileList: [], // 默认已经上传的文件列表
    // fileList: [], // 已上传的文件列表,多用于onChange事件里
    headers: {}, // 设置上传的请求头部，IE10 以上有效
    name: 'file', // 发到后台的文件参数名
    onChange: () => {
    }, // 当上传状态改变之后执行的回调函数
    onRemove: () => {
    }, // 点击移除文件时的回调，返回值为 false 时不移除。支持返回一个 Promise 对象，Promise 对象 resolve(false) 或 reject 时不移除
    mergeFiles: false, // 是否合并上传文件数组
    removeText: '', // 移除文件的文本
    downloadText: '', // 下载文件的文本
    onPreview: () => {
    }, // 点击文件链接或预览图标时的回调
    onDownload: null,
    progress: {},
    directory: false,
    locale: 'zh-cn',
    xsrf: true,
    dragable: false
};

const Upload: React.FC<EntranceProps> = (props) => {
    const [fileList, setFileList] = useState<DefaultListArr[]>(props.fileList || props.defaultFileList || []);
    const [dragState, setDragState] = useState('drop');
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    const lastenterRef = useRef<any>(null);
    const customProgressRef = useRef<any>(null);
    const progressTimerRef = useRef<any>(null);
    const uploadRef = useRef<any>(null);
    const fileListRef = useRef<DefaultListArr[]>(fileList);

    // 优化：添加防抖和节流
    const progressUpdateCountRef = useRef(0);
    const lastProgressUpdateRef = useRef(0);

    // 基础函数定义
    const clearProgressTimer = useCallback(() => {
        clearInterval(progressTimerRef.current);
    }, []);

    const removeFileItem = useCallback((file: DefaultListArr, fileList: DefaultListArr[]) => {
        const matchKey = file.uid !== undefined ? 'uid' : 'name';
        const removed = fileList.filter(item => item[matchKey] !== file[matchKey]);
        if (removed.length === fileList.length) {
            return null;
        }
        return removed;
    }, []);

    // 优化：使用 useMemo 缓存 maxCount 逻辑
    const processFileList = useMemo(() => {
        return (cloneList: DefaultListArr[], maxCount?: number) => {
            if (maxCount === 1) {
                return cloneList.slice(-1);
            } else if (maxCount && maxCount !== 1) {
                return cloneList.slice(0, maxCount);
            }
            return cloneList;
        };
    }, []);

    const onChange = useCallback((info: any) => {
        let {maxCount} = props;
        let cloneList = [...info.fileList];

        if (!('fileList' in props)) {
            cloneList = processFileList(cloneList, maxCount);
            setFileList(cloneList);
            fileListRef.current = cloneList;
        }

        const onChange = props.onChange;
        if (onChange) {
            onChange(info);
        }
    }, [props.maxCount, props.onChange, processFileList]);

    // 优化：progress 更新节流
    const onProgress = useCallback((e: any, file: DefaultListArr) => {
        const now = Date.now();
        // 节流：每100ms最多更新一次进度
        if (now - lastProgressUpdateRef.current < 100) {
            return;
        }
        lastProgressUpdateRef.current = now;

        customProgressRef.current = e;
        let targetItem = getFileItem(file, fileListRef.current);
        if (!targetItem) {
            return;
        }
        targetItem.percent = e.percent;
        onChange({
            event: e,
            file: targetItem,
            fileList: fileListRef.current,
        });
    }, [onChange]);

    // 优化：减少 progress 更新频率
    const autoUpdateProgress = useCallback((_: any, file: DefaultListArr) => {
        const getPercent = genPercentAdd();
        let curPercent = 0;
        progressTimerRef.current = setInterval(() => {
            curPercent = getPercent(curPercent);
            progressUpdateCountRef.current++;

            // 减少更新频率：每3次更新才触发一次
            if (progressUpdateCountRef.current % 3 === 0) {
                onProgress({
                    percent: curPercent,
                }, file);
            }
        }, 300); // 增加间隔时间从200ms到300ms
    }, [onProgress]);

    const handleRemove = useCallback((file: DefaultListArr) => {
        const {onRemove} = props;
        const {status} = file;

        file.status = 'removed';

        Promise.resolve(typeof onRemove === 'function' ? onRemove(file) : onRemove).then((ret: any) => {
            if (ret === false) {
                file.status = status;
                return;
            }

            const removedFileList = removeFileItem(file, fileListRef.current);
            if (removedFileList) {
                onChange({
                    file,
                    fileList: removedFileList,
                });
            }
        });
    }, [props.onRemove, removeFileItem, onChange]);

    // 优化：批量处理文件添加
    const onStart = useCallback((file: any) => {
        let targetItem;
        let nextFileList = fileListRef.current.concat();

        if (file.length > 0) {
            targetItem = file.map((f: any) => {
                const fileObject = fileToObject(f);
                fileObject.status = 'uploading';
                return fileObject;
            });
            nextFileList = nextFileList.concat(targetItem);
        } else {
            targetItem = fileToObject(file);
            targetItem.status = 'uploading';
            nextFileList.push(targetItem as DefaultListArr);
        }

        // 批量更新，减少重渲染
        onChange({
            file: targetItem,
            fileList: nextFileList,
        });
        // fix ie progress
        if (!window.FormData) {
            autoUpdateProgress(0, targetItem);
        }
        if (customProgressRef.current && Object.keys(customProgressRef.current).length === 1) {
            onProgress(customProgressRef.current, targetItem)
        }
    }, [onChange, autoUpdateProgress, onProgress]);

    const onSuccess = useCallback((response: any, file: DefaultListArr) => {
        clearProgressTimer();
        try {
            if (typeof response === 'string') {
                response = JSON.parse(response);
            }
        } catch (e) { /* do nothing */ }

        let targetItem = getFileItem(file, fileListRef.current);
        if (!targetItem) {
            return;
        }
        targetItem.status = 'done';
        targetItem.response = response;
        onChange({
            file: targetItem,
            fileList: fileListRef.current,
        });
    }, [onChange, clearProgressTimer]);

    const onError = useCallback((error: any, response: any, file: DefaultListArr) => {
        clearProgressTimer();
        let targetItem = getFileItem(file, fileListRef.current);
        if (!targetItem) {
            return;
        }
        targetItem.error = error;
        targetItem.response = response;
        targetItem.status = 'error';
        handleRemove(targetItem);
    }, [clearProgressTimer, handleRemove]);

    const handleManualRemove = useCallback((file: DefaultListArr) => {
        if (uploadRef.current) {
            uploadRef.current.abort(file);
        }
        file.status = 'removed';
        handleRemove(file);
    }, [handleRemove]);

    // 优化：fileList 变化检测
    useEffect(() => {
        if ('fileList' in props) {
            const newFileList = props.fileList || [];
            // 浅比较，避免不必要的更新
            if (JSON.stringify(newFileList) !== JSON.stringify(fileListRef.current)) {
                setFileList(newFileList);
                fileListRef.current = newFileList;
            }
        }
    }, [props.fileList]);

    const onDragEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
        lastenterRef.current = e.target; // 记录最后进入的元素
        setDragState('dragover');
        let { enterDragger } = props;
        if (enterDragger) {
            enterDragger();
        }
    }, [props.enterDragger]);

    const onDragLeave = useCallback((e: React.MouseEvent<HTMLElement>) => {
        // 如果此时退的元素是最后进入的元素，说明是真正退出了`drag-zone`元素
        if (lastenterRef.current === e.target) {
            setDragState(e.type);
            e.stopPropagation();
            e.preventDefault();
            let { leaveDragger } = props;
            if (leaveDragger) {
                leaveDragger();
            }
        }
    }, [props.leaveDragger]);

    const onFileDrop = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setDragState(e.type);
        let { leaveDragger } = props;
        if (leaveDragger) {
            leaveDragger();
        }
    }, [props.leaveDragger]);

    const handlePreview = useCallback((file: any) => {
        const {onPreview, preventDefaultPreview} = props;
        var displayPreview = () => {
            setPreviewImage(file.url || file.thumbUrl);
            setPreviewVisible(true);
        }

        if (preventDefaultPreview === false) {
            if (!file.url && !file.thumbUrl) {
                getBase64(file.originFileObj).then(displayPreview)
            } else {
                displayPreview()
            }
        }

        if (onPreview && Object.prototype.toString.call(onPreview) === "[object Function]") {
            onPreview(file);
        }
    }, [props.onPreview, props.preventDefaultPreview]);

    const onDownload = useCallback((file: DefaultListArr) => {
        let {onDownload} = props;
        if (typeof onDownload === 'function') {
            onDownload(file);
        } else if (file.url) {
            window.open(file.url);
        }
    }, [props.onDownload]);

    const handleCancel = useCallback(() => setPreviewVisible(false), []);

    // 优化：使用 useMemo 缓存计算结果
    const memoizedProps = useMemo(() => {
        const {
            clsPrefix = '', showUploadList, listType,
            type, disabled, children, className, removeText, downloadText, locale, progress, fieldid, uploadClassName, iconRender, itemRender, onDrag, dragable
        } = props;

        const {showRemoveIcon, showDownloadIcon, showPreviewIcon} = showUploadList as {
            showPreviewIcon?: boolean,
            showRemoveIcon?: boolean,
            showDownloadIcon?: boolean
        };

        const _removeText = getChildrenText(removeText).join('')
        const _downloadText = getChildrenText(downloadText).join('')

        return {
            clsPrefix, showUploadList, listType, type, disabled, children, className,
            showRemoveIcon, showDownloadIcon, showPreviewIcon, progress, fieldid,
            uploadClassName, iconRender, itemRender, onDrag, dragable,
            _removeText, _downloadText, locale
        };
    }, [props]);

    const {
        clsPrefix, showUploadList, listType, type, disabled, children, className,
        showRemoveIcon, showDownloadIcon, showPreviewIcon, progress, fieldid,
        uploadClassName, iconRender, itemRender, onDrag, dragable,
        _removeText, _downloadText, locale
    } = memoizedProps;

    let rcUploadProps = Object.assign({}, props, {
        removeText: _removeText,
        downloadText: _downloadText,
        onStart: onStart,
        onError: onError,
        onProgress: onProgress,
        onSuccess: onSuccess,
    });
    delete rcUploadProps.className;
    delete rcUploadProps.uploadClassName;

    const uploadList = showUploadList ? (
        <UploadList
            listType={listType}
            items={fileList}
            onPreview={handlePreview}
            onRemove={handleManualRemove}
            removeText={_removeText}
            downloadText={_downloadText}
            showRemoveIcon={!disabled && showRemoveIcon}
            showDownloadIcon={!disabled && showDownloadIcon}
            onDownload={onDownload}
            showPreviewIcon={showPreviewIcon}
            locale={locale}
            progress={progress}
            fieldid={fieldid}
            showUploadList={showUploadList}
            iconRender = {iconRender}
            itemRender={itemRender}
            onDrag={onDrag}
            dragable={dragable}
            disabled={disabled}
        />
    ) : null;
    let adapterNid = getNid(props) // 适配nid、uitype
    let rootDomClass = classNames({
        [`${clsPrefix}-wrapper`]: true,
        [`${className}`]: className
    })

    if (type === 'drag') {
        const dragCls = classNames(clsPrefix, {
            [`${clsPrefix}-drag`]: true,
            [`${clsPrefix}-drag-uploading`]: fileList.some(file => file.status === 'uploading'),
            [`${clsPrefix}-drag-hover`]: dragState === 'dragover',
            [`${clsPrefix}-disabled`]: disabled,
            [`${uploadClassName}`]: uploadClassName
        });
        return (
            <span className={rootDomClass} {...adapterNid} fieldid={fieldid}>
                <div
                    className={dragCls}
                    onDrop={onFileDrop}
                    onDragLeave={onDragLeave}
                    onDragEnter={onDragEnter}
                >
                    <RcUpload {...rcUploadProps} ref={uploadRef} className={`${clsPrefix}-btn`}>
                        <div className={`${clsPrefix}-drag-container`} fieldid={fieldid ? fieldid + "_drag_container" : undefined}>
                            {children}
                        </div>
                    </RcUpload>
                </div>
                {uploadList}
            </span>
        );
    }

    const uploadButtonCls = classNames(clsPrefix, {
        [`${clsPrefix}-select`]: true,
        [`${clsPrefix}-select-${listType}`]: true,
        [`${clsPrefix}-disabled`]: disabled,
        [`${uploadClassName}`]: uploadClassName
    });

    const uploadButton = (
        <div className={uploadButtonCls} style={{display: children ? '' : 'none'}} fieldid={fieldid} onDrop={onFileDrop} onDragLeave={onDragLeave} onDragEnter={onDragEnter}>
            <RcUpload {...rcUploadProps} ref={uploadRef}/>
        </div>
    );

    if (listType === 'picture-card') {
        const local = getLangInfo(locale, i18n, 'upload');
        return (
            <div {...adapterNid} className={`${clsPrefix}-wrapper`}>
                <span className={className}>
                    {uploadList}
                    {uploadButton}
                </span>
                <Modal show={previewVisible} onHide={handleCancel} onOk={handleCancel} locale={locale}>
                    <Modal.Header closeButton>
                        <Modal.Title>{local.langMap.preview}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <img alt="example" style={{width: '100%'}} src={previewImage}/>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }

    return (
        <span className={rootDomClass} {...adapterNid}>
            {uploadButton}
            {uploadList}
        </span>
    );
};

interface ComponentUpload extends React.FC<EntranceProps> {
	Dragger: typeof Dragger;
}

(Upload as any).Dragger = Dragger;
(Upload as any).defaultProps = defaultProps;

export default WithConfigConsumer()(Upload) as ComponentUpload;